import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import FolderIcon from '@mui/icons-material/Folder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Filter } from '../page-component/PageTypes';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import * as yup from 'yup';
import { UniqueIdentifier, useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TextField } from '../fields';
import { UseFormWatch } from 'react-hook-form';

export interface GroupItem {
  id: string;
  name: string;
  type: 'group';
  orderIndex: number;
}

export interface FilterItem {
  id: string;
  filterId: string;
  filterName: string;
  groupId?: string | null;
  orderIndex: number;
  type: 'filter';
  [key: string]: any;
}

export const DeleteConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  groupName,
  filterCount,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  groupName: string;
  filterCount: number;
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        sx: {
          borderRadius: '8px',
        },
        onClick: (e) => e.stopPropagation(),
      }}
    >
      <DialogTitle
        sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <DeleteIcon sx={{ color: '#d32f2f' }} />
        Delete Group
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete the group{' '}
          <strong>"{groupName}"</strong>?
        </Typography>

        {filterCount > 0 && (
          <Box
            sx={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeeba',
              borderRadius: '4px',
              padding: '12px',
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: '#856404' }}>
              ⚠️ This group contains <strong>{filterCount}</strong> filter
              {filterCount !== 1 ? 's' : ''}.
              {filterCount === 1 ? ' It' : ' They'} will be moved to ungrouped
              filters.
            </Typography>
          </Box>
        )}

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ fontSize: '0.8125rem' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          sx={{ fontSize: '0.8125rem' }}
        >
          Delete Group
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AddToFavoritesModal = ({
  open,
  onClose,
  mode,
  formControl,
  onSave,
  onCancel,
  availableGroups,
  selectedGroupId,
  onGroupChange,
  onCreateGroup,
  watch,
  isDirty,
}: {
  open: boolean;
  onClose: () => void;
  mode: 'normal' | 'edit';
  formControl: any;
  onSave: () => void;
  onCancel: () => void;
  availableGroups: GroupItem[];
  selectedGroupId: string | null;
  onGroupChange: (groupId: string | null) => void;
  onCreateGroup: (groupName: string) => Promise<GroupItem | null>;
  watch: UseFormWatch<any>;
  isDirty: boolean;
}) => {
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filterNameValue, setFilterNameValue] = useState('');

  React.useEffect(() => {
    if (watch) {
      const subscription = watch((value: any, { name }: any) => {
        if (name === 'filterName' || !name) {
          setFilterNameValue(value?.filterName || '');
        }
      });

      const currentValue = formControl.getValues?.('filterName') || '';
      setFilterNameValue(currentValue);

      return () => subscription?.unsubscribe?.();
    } else {
      const currentValue = formControl?.getValues?.('filterName') || '';
      setFilterNameValue(currentValue);
    }
  }, [formControl, open]);

  React.useEffect(() => {
    if (open) {
      const currentValue = watch?.('filterName') || '';

      setFilterNameValue(currentValue);
      setIsLoading(false);
    } else {
      setShowCreateGroup(false);
      setNewGroupName('');
      setIsLoading(false);
    }
  }, [open, formControl, watch]);

  const isFilterNameValid = Boolean(
    filterNameValue && filterNameValue.trim().length > 0
  );

  const handleCreateNewGroup = async () => {
    if (newGroupName.trim()) {
      const newGroup = await onCreateGroup(newGroupName.trim());
      if (newGroup) {
        onGroupChange(newGroup.id);
        setNewGroupName('');
        setShowCreateGroup(false);
      }
    }
  };

  const handleSave = async () => {
    if (!isFilterNameValid) {
      return;
    }

    try {
      setIsLoading(true);
      await onSave();
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        sx: {
          borderRadius: '8px',
        },
        onClick: (e) => e.stopPropagation(),
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {mode === 'edit' ? 'Edit Filter' : 'Add to Favorites'}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          id="filterName"
          name="filterName"
          label="Filter Name"
          control={formControl}
          required
          errors={
            !isFilterNameValid && filterNameValue !== ''
              ? ['Filter name is required']
              : []
          }
          sx={{ width: '100%', mb: 2 }}
          autoFocus
        />

        {/* Group Selection Section */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Add to Group (Optional)
          </Typography>

          {/* No Group Option */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <input
              type="radio"
              id="no-group"
              name="filterGroup"
              checked={selectedGroupId === null}
              onChange={() => onGroupChange(null)}
              style={{ marginRight: '8px' }}
              disabled={isLoading}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No Group (Ungrouped)
            </Typography>
          </Box>

          {/* Existing Group Options */}
          {availableGroups.map((group) => (
            <Box
              key={group.id}
              sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
            >
              <input
                type="radio"
                id={`group-${group.id}`}
                name="filterGroup"
                checked={selectedGroupId === group.id}
                onChange={() => onGroupChange(group.id)}
                style={{ marginRight: '8px' }}
                disabled={isLoading}
              />
              <Typography variant="body2">
                <FolderIcon sx={{ fontSize: '16px', mr: 0.5 }} /> {group.name}
              </Typography>
            </Box>
          ))}

          {/* Create New Group Section */}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            {!showCreateGroup ? (
              <Button
                variant="text"
                size="small"
                onClick={() => setShowCreateGroup(true)}
                sx={{ fontSize: '12px', textTransform: 'none' }}
                disabled={isLoading}
              >
                <AddIcon sx={{ fontSize: '16px', mr: 0.5 }} />
                Create New Group
              </Button>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Create and add to new group:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <MuiTextField
                    placeholder="Enter group name..."
                    size="small"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCreateNewGroup();
                      } else if (e.key === 'Escape') {
                        setNewGroupName('');
                        setShowCreateGroup(false);
                      }
                    }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '12px',
                      },
                    }}
                    autoFocus
                    disabled={isLoading}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleCreateNewGroup}
                    disabled={!newGroupName.trim() || isLoading}
                    sx={{
                      fontSize: '10px',
                      minWidth: '45px',
                      height: '32px',
                    }}
                  >
                    Add
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setNewGroupName('');
                      setShowCreateGroup(false);
                    }}
                    sx={{
                      fontSize: '10px',
                      minWidth: '45px',
                      height: '32px',
                    }}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ fontSize: '0.8125rem' }}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          data-testId="filters-save-favourite"
          variant="contained"
          sx={{
            fontSize: '0.8125rem',
            backgroundColor: '#3F51B5',
            '&:disabled': {
              backgroundColor: '#3F51B5',
              opacity: 0.6,
            },
          }}
          onClick={handleSave}
          disabled={!isDirty || !isFilterNameValid || isLoading}
        >
          {isLoading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const SortableFilterItem = ({
  filter,
  onApply,
  onEdit,
  onDelete,
}: {
  filter: FilterItem;
  onApply: (filter: FilterItem) => void;
  onEdit: (filter: FilterItem) => void;
  onDelete: (filterId: string) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: filter.id,
    data: {
      type: 'filter',
      filter,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: '12px 16px',
        margin: '4px 0',
        backgroundColor: isDragging
          ? theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.12)'
            : 'rgba(255,255,255,0.8)'
          : theme.palette.background.paper,
        borderRadius: '4px',
        border: isDragging
          ? '2px dashed #1976d2'
          : `1px solid ${theme.palette.divider}`,
        boxShadow: isDragging
          ? '0 8px 24px rgba(0,0,0,0.3)'
          : '0 1px 3px rgba(0,0,0,0.1)',
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.8 : 1,
        zIndex: isDragging ? 1000 : 'auto',
        position: 'relative',
        '&:hover': !isDragging
          ? {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.08)'
                  : '#f5f5f5',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            }
          : {},
      })}
    >
      <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
        {filter.filterName}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          type="button"
          size="small"
          data-testid={`apply-favourites-${filter.filterName}`}
          onClick={(e) => {
            e.stopPropagation();
            onApply(filter);
          }}
          sx={{ fontSize: '12px', textTransform: 'none' }}
          disabled={isDragging}
        >
          Apply Filter
        </Button>
        <ButtonGroup variant="text" aria-label="text button group">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(filter);
            }}
            data-testid={`edit-favourites-${filter.filterName}`}
            variant="text"
            sx={{ fontSize: '10px', minWidth: '55px', height: '24px' }}
            disabled={isDragging}
          >
            <EditIcon sx={{ width: '18px', height: '18px' }} />
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(filter.filterId);
            }}
            data-testid={`delete-favourites-${filter.filterName}`}
            variant="text"
            sx={{ fontSize: '10px', minWidth: '55px', height: '24px' }}
            disabled={isDragging}
          >
            <DeleteIcon sx={{ width: '18px', height: '18px' }} />
          </Button>
        </ButtonGroup>
      </Box>
    </Box>
  );
};

export const SortableGroupItem = ({
  group,
  isOver,
  activeId,
  isCollapsed,
  onToggleCollapse,
  isFilterBeingDragged,
  onEditGroup,
  onDeleteGroup,
  isEditing,
  editValue,
  onEditValueChange,
  onSaveEdit,
  onCancelEdit,
}: {
  group: GroupItem;
  isOver: boolean;
  activeId: UniqueIdentifier | null;
  isCollapsed: boolean;
  onToggleCollapse: (groupId: string) => void;
  isFilterBeingDragged: boolean;
  onEditGroup: (group: GroupItem) => void;
  onDeleteGroup: (groupId: string) => void;
  isEditing: boolean;
  editValue: string;
  onEditValueChange: (value: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging: isGroupDragging,
  } = useSortable({
    id: group.id,
    data: {
      type: 'group',
      group,
    },
    disabled: isFilterBeingDragged || isEditing,
  });

  const { setNodeRef: setDropRef } = useDroppable({
    id: group.id,
    data: {
      type: 'group',
      group,
    },
  });

  const setNodeRef = useCallback(
    (node: HTMLElement | null) => {
      setSortableRef(node);
      setDropRef(node);
    },
    [setSortableRef, setDropRef]
  );

  const isDragging = activeId !== null;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(isFilterBeingDragged || isEditing ? {} : listeners)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: isOver
          ? '#e8f5e8'
          : isDragging
          ? '#f0f8ff'
          : isGroupDragging
          ? 'rgba(255,255,255,0.8)'
          : '#f8f9fa',
        borderRadius: '6px',
        border: isOver
          ? '2px dashed #4caf50'
          : isDragging
          ? '2px dashed #1976d2'
          : isGroupDragging
          ? '2px dashed #ff9800'
          : '2px solid #e0e0e0',
        cursor:
          isFilterBeingDragged || isEditing
            ? 'default'
            : isGroupDragging
            ? 'grabbing'
            : 'grab',
        transition: 'all 0.2s ease',
        transform: isDragging && !isOver ? 'scale(1.02)' : 'scale(1)',
        boxShadow: isDragging
          ? '0 4px 12px rgba(25, 118, 210, 0.15)'
          : isGroupDragging
          ? '0 4px 12px rgba(255, 152, 0, 0.15)'
          : 'none',
        opacity: isGroupDragging ? 0.8 : 1,
        '&:hover': {
          backgroundColor: isFilterBeingDragged
            ? isOver
              ? '#e8f5e8'
              : '#f8f9fa'
            : isOver
            ? '#e8f5e8'
            : isDragging
            ? '#e3f2fd'
            : '#e3f2fd',
          borderColor: isOver
            ? '#4caf50'
            : isFilterBeingDragged
            ? '#e0e0e0'
            : '#1976d2',
        },
      }}
    >
      <Button
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onToggleCollapse(group.id);
        }}
        sx={{ minWidth: '24px', width: '24px', height: '24px', mr: 1, p: 0 }}
        disabled={isEditing}
      >
        {isCollapsed ? (
          <ExpandMoreIcon sx={{ fontSize: '16px' }} />
        ) : (
          <ExpandLessIcon sx={{ fontSize: '16px' }} />
        )}
      </Button>

      {/* Group Name - Editable or Display */}
      {isEditing ? (
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1 }}>
          <MuiTextField
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSaveEdit();
              } else if (e.key === 'Escape') {
                onCancelEdit();
              }
            }}
            size="small"
            autoFocus
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                height: '32px',
              },
            }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={onSaveEdit}
            disabled={!editValue.trim()}
            sx={{
              fontSize: '10px',
              minWidth: '40px',
              height: '32px',
            }}
          >
            Save
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={onCancelEdit}
            sx={{
              fontSize: '10px',
              minWidth: '40px',
              height: '32px',
            }}
          >
            Cancel
          </Button>
        </Box>
      ) : (
        <>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 'bold', color: 'text.primary', flex: 1 }}
          >
            <FolderIcon
              sx={{
                width: '18px',
                height: '18px',
                verticalAlign: 'middle',
                mr: 0.5,
              }}
            />{' '}
            {group.name}
          </Typography>

          {/* Group Action Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Show filter count when collapsed */}
            {isCollapsed && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '10px', mr: 1 }}
              >
                {/* You'd pass the filter count as a prop */}
              </Typography>
            )}

            {isOver && (
              <Typography
                variant="caption"
                sx={{
                  color: '#4caf50',
                  fontWeight: 'bold',
                  fontSize: '11px',
                  mr: 1,
                }}
              >
                <MoveToInboxIcon
                  sx={{
                    width: '18px',
                    height: '18px',
                    verticalAlign: 'middle',
                    mr: 0.5,
                  }}
                />{' '}
                Drop filter here
              </Typography>
            )}

            {/* Edit and Delete Buttons */}
            {!isOver && !isDragging && !isFilterBeingDragged && (
              <ButtonGroup variant="text" size="small">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditGroup(group);
                  }}
                  sx={{
                    fontSize: '10px',
                    minWidth: '32px',
                    height: '24px',
                    padding: '2px 4px',
                  }}
                  title="Edit group name"
                >
                  <EditIcon sx={{ width: '18px', height: '18px' }} />
                </Button>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteGroup(group.id);
                  }}
                  sx={{
                    fontSize: '10px',
                    minWidth: '32px',
                    height: '24px',
                    padding: '2px 4px',
                    color: '#d32f2f',
                  }}
                  title="Delete group"
                >
                  <DeleteIcon sx={{ width: '18px', height: '18px' }} />
                </Button>
              </ButtonGroup>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export const EmptyGroupDropZone = ({
  groupId,
  isOver,
  activeId,
}: {
  groupId: string;
  isOver: boolean;
  activeId: UniqueIdentifier | null;
}) => {
  const { setNodeRef } = useDroppable({
    id: `${groupId}-dropzone`,
    data: {
      type: 'group-dropzone',
      groupId: groupId,
    },
  });

  const isDragging = activeId !== null;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: '60px',
        margin: '8px 0',
        marginLeft: '24px',
        backgroundColor: isOver
          ? '#e8f5e8'
          : isDragging
          ? '#f9f9f9'
          : '#fafafa',
        borderRadius: '6px',
        border: isOver
          ? '2px dashed #4caf50'
          : isDragging
          ? '2px dashed #ddd'
          : '2px dashed #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        cursor: 'default',
      }}
    >
      {isOver ? (
        <Typography
          variant="body2"
          sx={{
            color: '#4caf50',
            fontWeight: 'bold',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <MoveToInboxIcon
            sx={{
              width: '18px',
              height: '18px',
              verticalAlign: 'middle',
              mr: 0.5,
            }}
          />{' '}
          Drop filter to add to group
        </Typography>
      ) : isDragging ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.disabled',
            fontSize: '12px',
            fontStyle: 'italic',
          }}
        >
          Drop filters here to add to group
        </Typography>
      ) : (
        <Typography
          variant="body2"
          sx={{
            color: 'text.disabled',
            fontSize: '12px',
            fontStyle: 'italic',
          }}
        >
          No filters in this group
        </Typography>
      )}
    </Box>
  );
};

export const schema = yup.object().shape({
  lossDate: yup
    .array()
    .test(
      'date_range_needed',
      'Both From and To dates are required',
      (value, context) => {
        if (
          value &&
          ((value[0] != null && value[1] == null) ||
            (value[1] != null && value[0] == null))
        ) {
          return false;
        }
        return true;
      }
    ),
  appointmentDate: yup
    .array()
    .test(
      'date_range_needed',
      'Both From and To dates are required',
      (value, context) => {
        if (
          value &&
          ((value[0] != null && value[1] == null) ||
            (value[1] != null && value[0] == null))
        ) {
          return false;
        }
        return true;
      }
    ),
  noticeDate: yup
    .array()
    .test(
      'date_range_needed',
      'Both From and To dates are required',
      (value, context) => {
        if (
          value &&
          ((value[0] != null && value[1] == null) ||
            (value[1] != null && value[0] == null))
        ) {
          return false;
        }
        return true;
      }
    ),
  receivedDate: yup
    .array()
    .test(
      'date_range_needed',
      'Both From and To dates are required',
      (value, context) => {
        if (
          value &&
          ((value[0] != null && value[1] == null) ||
            (value[1] != null && value[0] == null))
        ) {
          return false;
        }
        return true;
      }
    ),
  contingentCompanies: yup
    .array()
    .of(yup.string())
    .nullable()
    .when('employmentType', {
      is: 'contingent',
      then: yup
        .array()
        .of(yup.string())
        .min(1, 'Must choose at least one company')
        .required('Contingent Company is Required'),
    }),
});

export const setDefault = (
  buildDefaults: any,
  key: keyof Filter,
  filter: Filter
) => {
  if (filter?.type === 'date-range') {
    buildDefaults[key] = [null, null];
  } else if (
    (filter?.type?.includes('select') && filter.multi) ||
    filter.id === 'resourceAttributes'
  ) {
    buildDefaults[key] = [];
  } else {
    buildDefaults[key] = '';
  }
};

export const defaultFilters = (
  filters: {
    label: string;
    data: Filter[];
  }[]
) => {
  const buildDefaults: any = {};

  if (filters) {
    filters?.forEach((filter: { label: string; data: Filter[] }) => {
      filter?.data?.forEach((filter: Filter) => {
        if (filter?.id) {
          const key = `${filter?.id}` as keyof Filter;
          setDefault(buildDefaults, key, filter);

          if (filter.conditionalRender) {
            const conditionalFilterKey =
              `${filter.conditionalRender?.id}` as keyof Filter;
            setDefault(
              buildDefaults,
              conditionalFilterKey,
              filter.conditionalRender
            );
          }
        }
      });
    });
  }

  buildDefaults['resourceAttributes'] = [];

  return buildDefaults;
};
